/**
 * The track list the game shows. Three sources:
 *  - built-in: .xtrack.json files shipped in src/tracks, compiled through the
 *    normal import and compile pipeline (nothing about them is hard-coded in
 *    the engine; the catalog only adds a difficulty and a list order);
 *  - local: the player's own projects from the editor's store (created or
 *    imported), compiled the same way;
 *  - developer: the hand-built research levels (Movement Lab, Playground,
 *    Research Chambers, Violet Descent), kept apart from the main list.
 *
 * Every track's records are looked up under its baseline record key: its
 * gameplay fingerprint plus the shipped movement rules.
 */
import { compileTrack } from '../authoring/compile';
import { makeConfig } from '../core/Config';
import { importTrackText } from '../editor/importExport';
import { ProjectStore } from '../editor/ProjectStore';
import { LEVELS } from '../levels';
import { createRuleSet } from '../movement/experiments';
import { recordKeyFor } from '../simulation/Simulation';
import type { LevelData } from '../world/LevelData';
import cells from '../tracks/cells.xtrack.json?raw';
import undercroft from '../tracks/undercroft.xtrack.json?raw';
import cistern from '../tracks/cistern.xtrack.json?raw';
import halls from '../tracks/halls.xtrack.json?raw';
import ossuary from '../tracks/ossuary.xtrack.json?raw';

export type TrackSource = 'builtin' | 'training' | 'local' | 'developer';
export type Difficulty = 'Introductory' | 'Intermediate' | 'Advanced' | 'Showcase' | 'Training' | 'Unrated' | 'Developer';

export interface CatalogTrack {
  /** Stable catalog key: `builtin:<file>`, `local:<project id>` or `dev:<level key>`. */
  key: string;
  source: TrackSource;
  title: string;
  description: string;
  author: string;
  difficulty: Difficulty;
  /** Compiled level; null when the track does not compile (see `error`). */
  level: LevelData | null;
  error: string | null;
  /** Gameplay fingerprint ('' for developer levels, which have none). */
  fingerprint: string;
  /** Baseline record key (shipped movement rules); '' when the track cannot be played. */
  recordKey: string;
  /** Local tracks: the project id, for "open in editor". */
  projectId?: string;
}

interface BuiltinSpec {
  file: string;
  text: string;
  difficulty: Difficulty;
  source: 'builtin' | 'training';
}

/** Main list order: the learning path (all indoor since Phase 10), then the practice track. */
const BUILTINS: readonly BuiltinSpec[] = [
  { file: 'undercroft', text: undercroft, difficulty: 'Introductory', source: 'builtin' },
  { file: 'cistern', text: cistern, difficulty: 'Intermediate', source: 'builtin' },
  { file: 'halls', text: halls, difficulty: 'Advanced', source: 'builtin' },
  { file: 'ossuary', text: ossuary, difficulty: 'Advanced', source: 'builtin' },
  { file: 'cells', text: cells, difficulty: 'Training', source: 'training' },
];

const DEVELOPER: readonly { key: string; description: string }[] = [
  { key: 'lab', description: 'Phase 1–3 movement lab: speed range, slopes and the experiment stations.' },
  { key: 'playground', description: 'Phase 3E playground: one connected space for practising movement, with a timed route.' },
  { key: 'chambers', description: 'Phase 3 research chambers for curves, facets and ledges.' },
  { key: 'track', description: 'Violet Descent, the Phase 2 hand-built track.' },
];

export function baselineRecordKey(level: LevelData): string {
  return recordKeyFor(level, makeConfig(), createRuleSet());
}

/** Compiles one track file into a catalog entry. Never throws: problems become `error`. */
export function compileEntry(text: string, base: Pick<CatalogTrack, 'key' | 'source' | 'difficulty'> & { projectId?: string }): CatalogTrack {
  const imported = importTrackText(text);
  if (!imported.ok) return { ...base, title: 'Unreadable track', description: '', author: '', level: null, error: imported.errors[0] ?? 'the file is not a track', fingerprint: '', recordKey: '' };
  const doc = imported.doc;
  const meta = { ...base, title: doc.title, description: doc.description ?? '', author: doc.author ?? '' };
  let compiled;
  try {
    compiled = compileTrack(doc);
  } catch (err: unknown) {
    return { ...meta, level: null, error: `the compiler failed: ${err instanceof Error ? err.message : String(err)}`, fingerprint: '', recordKey: '' };
  }
  if (!compiled.level) return { ...meta, level: null, error: compiled.errors[0]?.message ?? 'the track does not compile', fingerprint: '', recordKey: '' };
  return { ...meta, level: compiled.level, error: null, fingerprint: compiled.fingerprint, recordKey: baselineRecordKey(compiled.level) };
}

let builtinCache: CatalogTrack[] | null = null;

export function builtinTracks(): CatalogTrack[] {
  builtinCache ??= BUILTINS.map((b) => compileEntry(b.text, { key: `builtin:${b.file}`, source: b.source, difficulty: b.difficulty }));
  return builtinCache;
}

export function developerLevels(): CatalogTrack[] {
  return DEVELOPER.filter((d) => Object.hasOwn(LEVELS, d.key)).map((d) => {
    const level = LEVELS[d.key]!;
    return { key: `dev:${d.key}`, source: 'developer', title: level.name, description: d.description, author: 'Xernous', difficulty: 'Developer', level, error: null, fingerprint: '', recordKey: baselineRecordKey(level) };
  });
}

/** The player's local projects, compiled one by one (yielding between them so the page stays responsive). */
export async function localTracks(store: ProjectStore | null): Promise<CatalogTrack[]> {
  if (!store) return [];
  const out: CatalogTrack[] = [];
  const metas = (await store.list()).sort((a, b) => b.updatedAt - a.updatedAt);
  for (const m of metas) {
    const base = { key: `local:${m.id}`, source: 'local' as const, difficulty: 'Unrated' as const, projectId: m.id };
    try {
      const { doc } = await store.load(m.id);
      out.push(compileEntry(JSON.stringify(doc), base));
    } catch (err: unknown) {
      out.push({ ...base, title: m.title, description: '', author: '', level: null, error: err instanceof Error ? err.message : String(err), fingerprint: '', recordKey: '' });
    }
    await new Promise((r) => setTimeout(r, 0));
  }
  return out;
}

/** The next built-in track in list order after `key` (the completion screen's "Next track"). */
export function nextTrack(key: string, tracks: readonly CatalogTrack[]): CatalogTrack | null {
  const main = tracks.filter((t) => t.source === 'builtin' && t.level);
  const i = main.findIndex((t) => t.key === key);
  return i >= 0 && i + 1 < main.length ? main[i + 1]! : null;
}
